import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import moment from 'moment';
import { Box, Chip, Divider, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

import { generateWarehouseDocumentLabelsNotificationData, getPodsNotificationData, getWarehouseDocumentNotificationData } from '../../../../../core/utils/notification_utils';
import useNotification from '../../../../../core/hooks/use_notification';
import { getWarehouseDocumentAction, getWarehouseDocumentState } from '../../../../../features/inventory/get_warehouse_document';
import { Button } from '../../../../common/button';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_DATE_ONLY_FORMAT, ROUTE_WAREHOUSE_DOCUMENTS } from '../../../../../core/constants';
import { getAllPodsAction } from '../../../../../features/pod/get_all_pods';
import { getAllWarehousesState } from '../../../../../features/inventory/get_all_warehouses';
import { generateWarehouseDocumentLabelsAction, generateWarehouseDocumentLabelsState } from '../../../../../features/inventory/generate_warehouse_document_labels';
import { getWarehouseName } from '../../../../common/utils/warehouse_utils';
import DetailsForm from './form';
import DetailsList from './list';
import PageHeader from '../../../../common/page_header';
import SectionPaper from '../../../../common/section_paper';

const InfoField = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="textSecondary" display="block" sx={ {
            mb: 0.5,
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '0.7rem',
            letterSpacing: '0.5px'
        } }>
            { label }
        </Typography>
        <Typography variant="body1" sx={ { fontWeight: 500, color: 'text.primary' } }>
            { value !== undefined && value !== null && value !== '' ? value : '-' }
        </Typography>
    </Box>
);

const WarehouseDocumentDetailsPage = () => {
    const { showErrorMessage, showNotification } = useNotification();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { warehouseDocumentId } = useParams();
    const location = useLocation();

    const getWarehouseDocument = useSelector(getWarehouseDocumentState);
    const getAllWarehouses = useSelector(getAllWarehousesState);
    const generateWarehouseDocumentLabels = useSelector(generateWarehouseDocumentLabelsState);

    const isMWDocument = getWarehouseDocument?.data?.documentType === 'MW';
    const isConfirmed = getWarehouseDocument?.data?.confirmed;

    const showForm = isMWDocument && !isConfirmed
        && (getAllWarehouses?.data?.find(warehouse => warehouse?.internalId === getWarehouseDocument?.data?.targetWarehouseId)?.pod?.podId
            || getAllWarehouses?.data?.find(warehouse => warehouse?.internalId === getWarehouseDocument?.data?.targetWarehouseId)?.pop?.popId);

    useEffect(() => {
        warehouseDocumentId && dispatch(getWarehouseDocumentAction(warehouseDocumentId))
            .then(response => showErrorMessage((getWarehouseDocumentNotificationData(response))));
    }, [warehouseDocumentId, dispatch, showErrorMessage]);

    useEffect(() => {
        dispatch(getAllPodsAction())
            .then(response => showErrorMessage(getPodsNotificationData(response)));
    }, [dispatch, showErrorMessage]);

    const onGenerateWarehouseDocumentLabels = () => {
        dispatch(generateWarehouseDocumentLabelsAction({ id: warehouseDocumentId }))
            .then(response => {
                showNotification(generateWarehouseDocumentLabelsNotificationData(response));
            });
    };

    const sourceWarehouse = getAllWarehouses?.data?.map(warehouse => ({
        value: warehouse?.internalId,
        label: getWarehouseName(warehouse)
    }))
        .find(warehouse => warehouse?.value === getWarehouseDocument?.data?.warehouseId)?.label;

    const targetWarehouse = getAllWarehouses?.data?.map(warehouse => ({
        value: warehouse?.internalId,
        label: getWarehouseName(warehouse)
    }))
        .find(warehouse => warehouse?.value === getWarehouseDocument?.data?.targetWarehouseId)?.label;

    const isLoading = getWarehouseDocument?.loading;

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>

            <PageHeader
                title={ getWarehouseDocument?.data?.name || "Szczegóły dokumentu" }
                showBack={ true }
                onBack={ () => navigate(ROUTE_WAREHOUSE_DOCUMENTS, { state: { queryParams: location?.state?.queryParams } }) }
                titleAdornment={
                    getWarehouseDocument?.data && (
                        <Stack direction="row" spacing={ 1 } sx={ { mt: { xs: 1, sm: 0 }, ml: { sm: 2 } } }>
                            <Chip
                                label={ getWarehouseDocument?.data?.documentType }
                                color="primary"
                                variant="outlined"
                                sx={ { fontWeight: 600 } }
                            />
                            { getWarehouseDocument?.data?.purposeType === 'RESERVATION' && (
                                <Chip label="Rezerwacje użytkowników (R)" color="success" variant="outlined"/>
                            ) }
                            <Chip
                                label={ isConfirmed ? "Zatwierdzony" : "Oczekuje na zatwierdzenie" }
                                color={ isConfirmed ? "success" : "warning" }
                                sx={ { fontWeight: 600 } }
                            />
                        </Stack>
                    )
                }
                actions={
                    <Button
                        isLoading={ generateWarehouseDocumentLabels?.loading }
                        onClick={ onGenerateWarehouseDocumentLabels }
                        startIcon={ <DownloadIcon/> }
                    >
                        Pobierz naklejki
                    </Button>
                }
            />

            { isLoading && (
                <Box width="100%">
                    <LinearProgress/>
                </Box>
            ) }

            { !isLoading && getWarehouseDocument?.data && (
                <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>
                    <SectionPaper title="Podsumowanie dokumentu">
                        <Grid container spacing={ 4 }>
                            <Grid item xs={ 12 } sm={ 3 }>
                                <InfoField
                                    label="Data utworzenia"
                                    value={ moment(getWarehouseDocument?.data?.issueDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_DATE_ONLY_FORMAT) }
                                />
                            </Grid>
                            <Grid item xs={ 12 } sm={ 3 }>
                                <InfoField
                                    label="Data dostawy"
                                    value={ getWarehouseDocument?.data?.deliveryDate ? moment(getWarehouseDocument?.data?.deliveryDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_DATE_ONLY_FORMAT) : undefined }
                                />
                            </Grid>
                            <Grid item xs={ 12 } sm={ 6 }>
                                <InfoField
                                    label="Kontrahent"
                                    value={ getWarehouseDocument?.data?.counterpartyName }
                                />
                            </Grid>

                            <Grid item xs={ 12 }>
                                <Divider/>
                            </Grid>

                            <Grid item xs={ 12 } sm={ 6 }>
                                <InfoField
                                    label={ isMWDocument ? 'Magazyn źródłowy' : 'Magazyn' }
                                    value={ sourceWarehouse }
                                />
                            </Grid>
                            { isMWDocument && (
                                <Grid item xs={ 12 } sm={ 6 }>
                                    <InfoField
                                        label="Magazyn docelowy"
                                        value={ targetWarehouse }
                                    />
                                </Grid>
                            ) }
                        </Grid>
                    </SectionPaper>

                    <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1 } }>
                        { showForm ? (
                            <DetailsForm
                                warehouseType={ getWarehouseDocument?.data?.purposeType === 'RESERVATION' ? 'POP' : 'POD' }
                                podId={ getWarehouseDocument?.data?.purposeType === 'RESERVATION' ? '' : getAllWarehouses?.data?.find(warehouse => warehouse?.internalId === getWarehouseDocument?.data?.targetWarehouseId)?.pod?.podId }
                                hubId={ getWarehouseDocument?.data?.purposeType !== 'RESERVATION' ? undefined : getAllWarehouses?.data?.find(warehouse => warehouse?.internalId === getWarehouseDocument?.data?.targetWarehouseId)?.pop?.hubId }
                                popId={ getWarehouseDocument?.data?.purposeType !== 'RESERVATION' ? undefined : getAllWarehouses?.data?.find(warehouse => warehouse?.internalId === getWarehouseDocument?.data?.targetWarehouseId)?.pop?.popId }
                                queryParams={ location?.state?.queryParams }
                            />
                        ) : (
                            <DetailsList/>
                        ) }
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default WarehouseDocumentDetailsPage;